import axios from 'axios';
import { notifyNewMessage, requestNotificationPermission } from '../utils/notifications';

const API_URL = process.env.REACT_APP_API_URL || '';

class NotificationService {
  constructor() {
    this.lastMessageIds = new Set();
    this.checkInterval = null;
    this.isInitialized = false;
    this.pushSubscription = null;
  }

  async initialize(currentUser) {
    if (this.isInitialized) return;
    
    this.currentUser = currentUser;
    await requestNotificationPermission();
    
    // Registriere Push-Subscription
    await this.registerPushNotification();
    
    // Initiale Nachrichtenliste laden
    await this.loadInitialMessages();
    
    // Starte Interval für neue Nachrichten
    this.startChecking();
    this.isInitialized = true;
    
    console.log('🔔 Notification Service initialisiert für:', currentUser);
  }

  async registerPushNotification() {
    try {
      // Prüfe ob Service Worker und Push API verfügbar sind
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('⚠️ Push-Benachrichtigungen nicht unterstützt');
        return;
      }

      // Hole Service Worker Registration
      const registration = await navigator.serviceWorker.ready;
      
      // Hole VAPID Public Key vom Server
      const response = await axios.get(`${API_URL}/push/vapid-public-key`);
      const vapidPublicKey = response.data.publicKey;
      
      // Konvertiere VAPID Key zu Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);
      
      // Erstelle Push-Subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });
      
      this.pushSubscription = subscription;
      
      // Sende Subscription an Server
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/push/subscribe`,
        subscription.toJSON(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Push-Benachrichtigungen aktiviert');
    } catch (error) {
      console.error('❌ Fehler bei Push-Registrierung:', error);
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async loadInitialMessages() {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Speichere alle aktuellen Message-IDs
      this.lastMessageIds = new Set(response.data.messages.map(msg => msg._id));
      console.log('📨 Initial geladen:', this.lastMessageIds.size, 'Nachrichten');
    } catch (error) {
      console.error('❌ Fehler beim Laden der initialen Nachrichten:', error);
    }
  }

  startChecking() {
    // Prüfe alle 5 Sekunden auf neue Nachrichten
    this.checkInterval = setInterval(() => this.checkForNewMessages(), 5000);
  }

  async checkForNewMessages() {
    try {
      const token = localStorage.getItem('token');
      if (!token || !this.currentUser) return;

      const response = await axios.get(`${API_URL}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allMessages = response.data.messages;
      
      // Finde NUR neue Nachrichten (die noch nicht in lastMessageIds sind)
      const newMessages = allMessages.filter(msg => !this.lastMessageIds.has(msg._id));
      
      if (newMessages.length > 0) {
        console.log('🔔 Neue Nachrichten gefunden:', newMessages.length);
        
        // Filtere nur Nachrichten, die AN den aktuellen Benutzer sind
        const messagesForMe = newMessages.filter(msg => 
          (msg.receiver_id === this.currentUser.username || msg.receiver_id === 'all') &&
          msg.sender_id !== this.currentUser.username
        );
        
        console.log('📬 Nachrichten für mich:', messagesForMe.length);
        
        // WICHTIG: Update lastMessageIds BEVOR wir Benachrichtigungen senden
        // So werden keine doppelten Benachrichtigungen gesendet
        allMessages.forEach(msg => this.lastMessageIds.add(msg._id));
        
        // Zeige Benachrichtigungen nur für neue Nachrichten an mich
        messagesForMe.forEach(msg => {
          const preview = msg.message_type === 'text' 
            ? (msg.content?.substring(0, 50) || '') + (msg.content?.length > 50 ? '...' : '')
            : msg.message_type === 'image' ? '📷 Bild' : '📄 Datei';
          
          console.log('🔊 Benachrichtigung:', msg.sender_name || msg.sender_id, '-', preview);
          notifyNewMessage(msg.sender_name || msg.sender_id, preview);
        });
      } else {
        // Update lastMessageIds auch wenn keine neuen Nachrichten
        allMessages.forEach(msg => this.lastMessageIds.add(msg._id));
      }
    } catch (error) {
      console.error('❌ Fehler beim Prüfen neuer Nachrichten:', error);
    }
  }

  stopChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('🛑 Notification Service gestoppt');
    }
  }

  destroy() {
    this.stopChecking();
    this.lastMessageIds.clear();
    this.isInitialized = false;
  }
}

// Singleton Instance
export const notificationService = new NotificationService();
